import { api } from './api.js';
import { ENDPOINTS } from '../config/endpoints.js';

export const scheduleService = {
  /**
   * Busca agendamentos da entidade
   */
  async getEntitySchedules(entityId, limit = null) {
    try {
      var url = `${ENDPOINTS.ENTITIES_SCHEDULES}?id=${entityId}`;
      if (limit) {
        url += `&limit=${limit}`;
      }
      const response = await api.get(url);
      return {
        success: true,
        schedules: response.data.response || []
      };
    } catch (error) {
      console.error('Error fetching schedules:', error);
      return {
        success: false,
        error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao buscar agendamentos'
      };
    }
  },

  /**
   * Busca detalhes de um agendamento específico
   */
  async getScheduleDetails(scheduleId) {
    try {
      const response = await api.get(`${ENDPOINTS.ENTITIES_SCHEDULE}?id=${scheduleId}`);
      return {
        success: true,
        schedule: response.data.response || null
      };
    } catch (error) {
      console.error('Error fetching schedule details:', error);
      return {
        success: false,
        error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao buscar detalhes do agendamento'
      };
    }
  },

  /**
   * Atualiza o status de um agendamento
   */
  async updateScheduleStatus(scheduleId, status, entityNotes = null) {
    try {
      const payload = {
        id: scheduleId,
        status: status
      };
      
      if (entityNotes) {
        payload.entityNotes = entityNotes;
      }
      
      const response = await api.put(`${ENDPOINTS.ENTITIES_SCHEDULE}`, payload);
      return {
        success: true,
        schedule: response.data.response || null
      };
    } catch (error) {
      console.error('Error updating schedule status:', error);
      return {
        success: false,
        error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao atualizar status do agendamento'
      };
    }
  },

  /**
   * Finaliza um agendamento salvando os dados da consulta
   * @param {string} scheduleId - ID do agendamento
   * @param {Object} consultationData - Dados da consulta atual
   * @param {string} consultationData.diagnosis - Diagnóstico
   * @param {string} consultationData.procedures - Procedimentos realizados
   * @param {string} consultationData.prescription - Prescrição/medicação
   * @param {string} consultationData.observations - Observações
   * @param {string} consultationData.returnDate - Data de retorno recomendada (YYYY-MM-DD)
   */
  async finalizeSchedule(scheduleId, petId, consultationData, consultationId = null, entityId = null) {
    try {
      const cd = consultationData;
      const payload = {
        ...(consultationId ? { id: consultationId } : { petId, scheduleId, ...(entityId ? { entityId } : {}) }),

        weight: cd.weight || null,
        temperature: cd.temperature || null,
        heartRate: cd.heartRate || null,
        respiratoryRate: cd.respiratoryRate || null,
        generalCondition: cd.generalCondition || null,
        hydrationLevel: cd.hydrationLevel || null,
        physicalExamNotes: cd.physicalExamNotes || null,

        chiefComplaint: cd.chiefComplaint || null,
        historyAndObservations: cd.historyAndObservations || null,

        clinicalDiagnosis: cd.clinicalDiagnosis || null,
        requestedExams: cd.requestedExams?.length ? cd.requestedExams : null,
        otherExams: cd.otherExams || null,

        generalGuidelines: cd.generalGuidelines || null,

        needsReturn: cd.needsReturn || null,
        returnDeadline: cd.returnDeadline || null,
        returnPriority: cd.returnPriority || null,
        returnNotes: cd.returnNotes || null,

        internalNotes: cd.internalNotes || null,
        active: true,
      };

      const response = consultationId
        ? await api.put(`${ENDPOINTS.CONSULTATIONS}`, payload)
        : await api.post(`${ENDPOINTS.CONSULTATIONS}`, payload);

      // Mark the schedule as completed
      await api.put(`${ENDPOINTS.ENTITIES_SCHEDULE}`, { id: scheduleId, status: 'completed' });

      return {
        success: true,
        consultation: response.data.response || null
      };
    } catch (error) {
      console.error('Error finalizing schedule:', error);
      return {
        success: false,
        error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao finalizar atendimento'
      };
    }
  },

  async saveDraft(scheduleId, petId, consultationData, consultationId = null, entityId = null) {
    try {
      const cd = consultationData;
      const payload = {
        ...(consultationId ? { id: consultationId } : { petId, scheduleId, ...(entityId ? { entityId } : {}) }),
        weight: cd.weight || null,
        temperature: cd.temperature || null,
        heartRate: cd.heartRate || null,
        respiratoryRate: cd.respiratoryRate || null,
        generalCondition: cd.generalCondition || null,
        hydrationLevel: cd.hydrationLevel || null,
        physicalExamNotes: cd.physicalExamNotes || null,
        chiefComplaint: cd.chiefComplaint || null,
        historyAndObservations: cd.historyAndObservations || null,
        clinicalDiagnosis: cd.clinicalDiagnosis || null,
        requestedExams: cd.requestedExams?.length ? cd.requestedExams : null,
        otherExams: cd.otherExams || null,
        generalGuidelines: cd.generalGuidelines || null,
        needsReturn: cd.needsReturn || null,
        returnDeadline: cd.returnDeadline || null,
        returnPriority: cd.returnPriority || null,
        returnNotes: cd.returnNotes || null,
        internalNotes: cd.internalNotes || null,
        active: false,
      };
      const response = consultationId
        ? await api.put(`${ENDPOINTS.CONSULTATIONS}`, payload)
        : await api.post(`${ENDPOINTS.CONSULTATIONS}`, payload);
      return {
        success: true,
        consultation: response.data.response || null
      };
    } catch (error) {
      console.error('Error saving draft:', error);
      return {
        success: false,
        error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao salvar rascunho'
      };
    }
  },

  async saveMedication(petId, consultationId, medData, medId = null) {
    try {
      const payload = {
        ...(medId ? { id: medId } : { petId, consultationId }),
        name:           medData.name,
        dosage:         medData.dosage || null,
        frequencyTimes: medData.frequencyTimes || null,
        frequencyType:  medData.frequencyType || null,
        durationTimes:  medData.durationTimes || '',
        durationType:   medData.durationType || '',
        startDate:      medData.startDate || null,
        endDate:        medData.endDate || null,
        status:         'active',
        reminder:       medData.reminder || false,
        notes:          medData.notes || null,
      };
      const response = medId
        ? await api.put(`${ENDPOINTS.CONSULTATIONS_MEDICATION}`, payload)
        : await api.post(`${ENDPOINTS.CONSULTATIONS_MEDICATION}`, payload);
      return { success: true, medication: response.data.response || null };
    } catch (error) {
      console.error('Error saving medication:', error);
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao salvar medicamento' };
    }
  },

  async deleteMedication(medId) {
    try {
      await api.put(`${ENDPOINTS.CONSULTATIONS_MEDICATION}`, { id: medId, active: false });
      return { success: true };
    } catch (error) {
      console.error('Error deleting medication:', error);
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao remover medicamento' };
    }
  }
};
